import React, { useState, useEffect } from 'react';
import { Settings, Lock, Shield, Key, Eye, EyeOff, Mail } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getViteApiUrl } from '../config/env';

const STORAGE_EMAIL_NOTIFICATIONS = 'jnugroup_email_notifications';

function PasswordField({
  id,
  label,
  value,
  onChange,
  placeholder,
  showPassword,
  onToggleShow,
  required = false,
  minLength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  showPassword: boolean;
  onToggleShow: () => void;
  required?: boolean;
  minLength?: number;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-5 w-5 text-gray-400" />
        </div>
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          minLength={minLength}
          className="block w-full pl-10 pr-12 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent outline-none placeholder-gray-400"
          style={{ colorScheme: 'light' }}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

export const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<'password' | '2fa' | 'notifications'>('password');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorMessage, setTwoFactorMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);

  const [emailNotifications, setEmailNotifications] = useState(true);
  const [notificationsSaving, setNotificationsSaving] = useState(false);
  const [notificationsMessage, setNotificationsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const twoFactorEnabled = user?.twoFactorEnabled ?? false;

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_EMAIL_NOTIFICATIONS);
      setEmailNotifications(stored === null || stored === 'true');
    } catch {
      setEmailNotifications(true);
    }
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'La nueva contraseña y la confirmación no coinciden.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'La nueva contraseña debe tener al menos 8 caracteres.' });
      return;
    }
    setPasswordLoading(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setPasswordMessage({ type: 'success', text: 'Contraseña actualizada correctamente.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordMessage({ type: 'error', text: err.message || 'Error al cambiar la contraseña.' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleStart2fa = async () => {
    setTwoFactorMessage(null);
    setQrCodeUrl(null);
    setTwoFactorCode('');
    setTwoFactorLoading(true);
    try {
      const data = await api.get2faSetup();
      setQrCodeUrl(data.qrCodeUrl);
    } catch (err: any) {
      setTwoFactorMessage({ type: 'error', text: err.message || 'Error al generar el código QR.' });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleVerify2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (twoFactorCode.length !== 6) return;
    setTwoFactorMessage(null);
    setTwoFactorLoading(true);
    try {
      await api.verify2fa(twoFactorCode);
      setTwoFactorMessage({ type: 'success', text: 'Autenticación en dos pasos activada.' });
      setQrCodeUrl(null);
      setTwoFactorCode('');
      window.dispatchEvent(new Event('user-updated'));
    } catch (err: any) {
      setTwoFactorMessage({ type: 'error', text: err.message || 'Código incorrecto.' });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFactorMessage(null);
    setDisableLoading(true);
    try {
      await api.disable2fa(disablePassword);
      setTwoFactorMessage({ type: 'success', text: 'Autenticación en dos pasos desactivada.' });
      setDisablePassword('');
      window.dispatchEvent(new Event('user-updated'));
    } catch (err: any) {
      setTwoFactorMessage({ type: 'error', text: err.message || 'Contraseña incorrecta.' });
    } finally {
      setDisableLoading(false);
    }
  };

  const handleToggleNotifications = async (enabled: boolean) => {
    setNotificationsSaving(true);
    setNotificationsMessage(null);
    try {
      localStorage.setItem(STORAGE_EMAIL_NOTIFICATIONS, String(enabled));
      setEmailNotifications(enabled);
      setNotificationsMessage({
        type: 'success',
        text: enabled ? 'Notificaciones por correo activadas.' : 'Notificaciones por correo desactivadas.',
      });
      setTimeout(() => setNotificationsMessage(null), 3000);
    } catch {
      setNotificationsMessage({ type: 'error', text: 'No se pudo guardar la preferencia.' });
    } finally {
      setNotificationsSaving(false);
    }
  };

  useEffect(() => {
    const onUserUpdated = () => {
      fetch(`${getViteApiUrl()}/auth/me`, {
        credentials: 'include',
      })
        .then((r) => (r.ok ? r.json() : null))
        .then(() => {
          window.location.reload();
        })
        .catch(() => {});
    };
    window.addEventListener('user-updated', onUserUpdated);
    return () => window.removeEventListener('user-updated', onUserUpdated);
  }, []);

  const tabClass = (section: typeof activeSection) =>
    `px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors ${
      activeSection === section
        ? 'border-[#25326B] text-[#25326B]'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Settings className="w-6 h-6 text-[#25326B]" />
        <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
      </div>
      <p className="text-gray-600">Actualiza tu contraseña, gestiona la autenticación en dos pasos y tus notificaciones.</p>

      <div className="flex gap-2 border-b border-gray-200">
        <button type="button" onClick={() => setActiveSection('password')} className={tabClass('password')}>
          Cambiar contraseña
        </button>
        <button type="button" onClick={() => setActiveSection('2fa')} className={tabClass('2fa')}>
          Autenticación en dos pasos (2FA)
        </button>
        <button type="button" onClick={() => setActiveSection('notifications')} className={tabClass('notifications')}>
          Notificaciones
        </button>
      </div>

      {activeSection === 'password' && (
        <Card title="Cambiar contraseña" className="max-w-md">
          <form onSubmit={handleChangePassword} className="space-y-4">
            <PasswordField
              id="current-password"
              label="Contraseña actual"
              value={currentPassword}
              onChange={setCurrentPassword}
              placeholder="••••••••"
              showPassword={showCurrentPassword}
              onToggleShow={() => setShowCurrentPassword((v) => !v)}
              required
            />
            <PasswordField
              id="new-password"
              label="Nueva contraseña"
              value={newPassword}
              onChange={setNewPassword}
              placeholder="Mínimo 8 caracteres"
              showPassword={showNewPassword}
              onToggleShow={() => setShowNewPassword((v) => !v)}
              required
              minLength={8}
            />
            <PasswordField
              id="confirm-password"
              label="Confirmar nueva contraseña"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="••••••••"
              showPassword={showConfirmPassword}
              onToggleShow={() => setShowConfirmPassword((v) => !v)}
              required
            />
            {passwordMessage && (
              <p className={`text-sm ${passwordMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {passwordMessage.text}
              </p>
            )}
            <button
              type="submit"
              disabled={passwordLoading}
              className="w-full py-3 px-4 bg-[#25326B] hover:bg-[#1a2550] text-white font-medium rounded-lg focus:ring-2 focus:ring-[#25326B] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {passwordLoading ? 'Guardando...' : 'Actualizar contraseña'}
            </button>
          </form>
        </Card>
      )}

      {activeSection === '2fa' && (
        <Card title="Autenticación en dos pasos (2FA)" className="max-w-md">
          <p className="text-sm text-gray-600 mb-4">
            La autenticación en dos pasos añade un código de 6 dígitos generado por una app (Google Authenticator, Authy, etc.) cada vez que inicies sesión.
          </p>
          {twoFactorMessage && (
            <p className={`text-sm mb-4 ${twoFactorMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {twoFactorMessage.text}
            </p>
          )}
          {twoFactorEnabled ? (
            <div>
              <p className="text-sm text-green-700 font-medium mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4" /> 2FA está activado
              </p>
              <form onSubmit={handleDisable2fa} className="space-y-3 mt-4">
                <PasswordField
                  id="disable-2fa-password"
                  label="Contraseña para desactivar 2FA"
                  value={disablePassword}
                  onChange={setDisablePassword}
                  placeholder="••••••••"
                  showPassword={showDisablePassword}
                  onToggleShow={() => setShowDisablePassword((v) => !v)}
                  required
                />
                <button
                  type="submit"
                  disabled={disableLoading}
                  className="w-full py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg disabled:opacity-50"
                >
                  {disableLoading ? 'Desactivando...' : 'Desactivar 2FA'}
                </button>
              </form>
            </div>
          ) : (
            <div>
              {!qrCodeUrl ? (
                <button
                  type="button"
                  onClick={handleStart2fa}
                  disabled={twoFactorLoading}
                  className="w-full py-3 px-4 bg-[#FA4999] hover:bg-[#FA4999]/90 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Key className="w-5 h-5" />
                  {twoFactorLoading ? 'Generando...' : 'Activar 2FA (mostrar código QR)'}
                </button>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-700">Escanea este código QR con tu app de autenticación:</p>
                  <div className="flex justify-center p-4 bg-white rounded-lg border border-gray-200">
                    <img src={qrCodeUrl} alt="Código QR 2FA" className="w-48 h-48" />
                  </div>
                  <form onSubmit={handleVerify2fa} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Código de 6 dígitos</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="w-full px-3 py-2 text-center text-lg tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent"
                    />
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={twoFactorLoading || twoFactorCode.length !== 6}
                        className="flex-1 py-2.5 px-4 bg-[#FA4999] hover:bg-[#FA4999]/90 text-white font-medium rounded-lg disabled:opacity-50"
                      >
                        {twoFactorLoading ? 'Verificando...' : 'Verificar y activar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setQrCodeUrl(null); setTwoFactorCode(''); setTwoFactorMessage(null); }}
                        className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {activeSection === 'notifications' && (
        <Card title="Notificaciones" className="max-w-md">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1 p-2 rounded-lg bg-gray-100">
              <Mail className="h-5 w-5 text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Notificaciones por correo</p>
              <p className="text-sm text-gray-500 mt-0.5">
                Recibir avisos por correo (estado de cuenta, documentos, etc.).
              </p>
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={emailNotifications}
                  disabled={notificationsSaving}
                  onClick={() => handleToggleNotifications(!emailNotifications)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:ring-2 focus:ring-[#25326B] focus:ring-offset-2 disabled:opacity-50 ${
                    emailNotifications ? 'bg-[#25326B]' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                      emailNotifications ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-700">
                  {emailNotifications ? 'Activadas' : 'Desactivadas'}
                </span>
              </div>
              {notificationsMessage && (
                <p className={`text-sm mt-2 ${notificationsMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {notificationsMessage.text}
                </p>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
